import { type ReactNode } from 'react';
import { type GameState, CLUE_MAX_LENGTH } from '@bangos/shared';
import { useT, useTranslateSpectrum } from '../i18n';
import { Scoreboard } from './Scoreboard';
import { RevealScreen } from './RevealScreen';
import { GameOverScreen } from './GameOverScreen';
import '../styles/gameplay.css';

interface RevealData {
  targetPosition: number;
  guessPosition: number;
  distance: number;
  points: number;
  team: 'blue' | 'red';
  guesserId: string;
}

interface PsychicViewProps {
  gameState: GameState;
  playerId: string;
  clueText: string;
  submitting: boolean;
  hasClue: boolean;
  submittedClue: { id: string; playerId: string; text: string; position: number } | null;
  revealData: RevealData | null;
  onPlayAgain?: () => void;
  onClueTextChange: (text: string) => void;
  onClueSubmit: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

export function PsychicView({
  gameState,
  playerId, // eslint-disable-line @typescript-eslint/no-unused-vars
  clueText,
  submitting,
  hasClue,
  submittedClue,
  revealData,
  onPlayAgain,
  onClueTextChange,
  onClueSubmit,
  onKeyDown,
}: PsychicViewProps): ReactNode {
  const t = useT();
  const tSpectrum = useTranslateSpectrum();
  const { phase, currentRound } = gameState;
  const translatedSpectrum = currentRound?.spectrum ? tSpectrum(currentRound.spectrum) : null;
  const targetPosition = currentRound?.targetPosition;

  // --- Main render ---
  return (
    <>
      {/* Scoreboard */}
      <Scoreboard blueScore={gameState.blueScore} redScore={gameState.redScore} />

      {/* Spectrum with psychic's secret dial marker */}
      {currentRound?.spectrum && translatedSpectrum && (
        <div className="spectrum-card">
          <div className="spectrum-title">{t('psychic.spectrumTitle')}</div>
          <div className="spectrum-concepts">
            <span className="spectrum-concept--left">{translatedSpectrum.left}</span>
            <span className="spectrum-concept--right">{translatedSpectrum.right}</span>
          </div>
          {/* Psychic's hidden dial position indicator — only shown to the psychic */}
          {targetPosition !== undefined && phase !== 'reveal' && phase !== 'gameOver' && (
            <div className="psychic-dial-track">
              <div className="psychic-dial-bar">
                <div
                  className="psychic-marker"
                  style={{ left: `${targetPosition}%` }}
                  title={t('reveal.targetPosition') + ': ' + targetPosition}
                />
              </div>
              <div className="psychic-dial-position">{t('psychic.dialPosition', { pos: targetPosition })}</div>
            </div>
          )}
        </div>
      )}

      {/* Clue submission — psychicTurn phase */}
      {phase === 'psychicTurn' && !hasClue && (
        <div className="clue-section">
          <div className="clue-psychic-notice">
            {t('psychic.psychicNotice')}
          </div>
          <div className="clue-input-container">
            <input
              type="text"
              className="clue-input"
              placeholder={t('psychic.cluePlaceholder')}
              value={clueText}
              onChange={(e) => onClueTextChange(e.target.value)}
              onKeyDown={onKeyDown}
              maxLength={CLUE_MAX_LENGTH}
              disabled={submitting}
              autoFocus
            />
            <button
              className="clue-submit-btn"
              onClick={onClueSubmit}
              disabled={!clueText.trim() || submitting}
            >
              {t('psychic.submit')}
            </button>
          </div>
          <div className="clue-char-counter">
            {clueText.length} / {CLUE_MAX_LENGTH}
          </div>
        </div>
      )}

      {/* Submitted clue display */}
      {hasClue && submittedClue && (
        <div className="clue-section">
          <div className="clue-display">
            {t('psychic.yourClue', { clue: submittedClue.text })}
          </div>
        </div>
      )}

      {/* Waiting during guessing phase */}
      {phase === 'guessing' && (
        <div className="clue-section clue-waiting">
          <div className="clue-waiting-text">
            {t('psychic.activeTeamGuessing')}
          </div>
        </div>
      )}

      {/* Reveal phase */}
      <RevealScreen gameState={gameState} revealData={revealData} submittedClue={submittedClue} playerId={playerId} />

      {/* Game Over phase */}
      <GameOverScreen gameState={gameState} onPlayAgain={onPlayAgain} />
    </>
  );
}
